import { theme } from '@ui/styles';
import moment from 'moment-timezone';

export type ViewersStatsVideoMeta = {
  id: string;
  channelId: string;
  title: string;
  startedAt: string;
  endedAt?: string;
};

/**
 * Minimal shape of the BFF country viewers payload used to build uPlot series.
 * Keeps this helper decoupled from the generated client export surface.
 */
export type ViewersStatsForChart = {
  metadata: {
    channels: Record<
      string,
      {
        id: string;
        title: string;
        thumbnailUrl?: string;
        isSelf?: boolean;
      }
    >;
    videos?: Record<string, ViewersStatsVideoMeta>;
  };
  timeSeries: Record<string, string | number>[];
};

export type YoutubeChannelLegendMeta = {
  channelId: string;
  label: string;
  thumbnailUrl?: string;
  color: string;
  isSelf: boolean;
};

/**
 * Returns all available channels sorted by label with a stable color assignment for legend/chart parity.
 */
export function buildYoutubeChannelLegendMeta(
  stats: ViewersStatsForChart | undefined,
  pastelColors: string[],
): YoutubeChannelLegendMeta[] {
  if (!stats?.metadata?.channels) {
    return [];
  }

  const channels = stats.metadata.channels;
  const orderedIds = Object.keys(channels).sort((a, b) => {
    const ta = channels[a]?.title ?? a;
    const tb = channels[b]?.title ?? b;
    return ta.localeCompare(tb);
  });

  return orderedIds.map((channelId, index) => {
    const channel = channels[channelId];

    return {
      channelId,
      label: channel?.title ?? channelId,
      thumbnailUrl: channel?.thumbnailUrl,
      color: channel?.isSelf ? theme.blueNewPastel : pastelColors[index % pastelColors.length],
      isSelf: Boolean(channel?.isSelf),
    };
  });
}

/**
 * Converts per-minute viewer points (channel id keys) into uPlot aligned data for the given channel ids.
 * Only keys present in `metadata.channels` are treated as channel series (video ids are ignored).
 */
export function transformViewersStatsForUplot(
  stats: ViewersStatsForChart | undefined,
  visibleChannelIds: string[],
  timezone: string,
  pastelColors: string[],
) {
  const emptySeries: {
    label: string;
    stroke: string;
    show: boolean;
    spanGaps: boolean;
    width: number;
    points: { show: boolean };
  }[] = [];

  if (!stats?.timeSeries?.length) {
    return {
      data: [new Float64Array([]), new Float64Array([])] as unknown as number[][],
      series: emptySeries,
      seriesChannelIds: [] as string[],
    };
  }

  const channels = stats.metadata.channels;
  const allChannelsMeta = buildYoutubeChannelLegendMeta(stats, pastelColors);
  const colorByChannelId = allChannelsMeta.reduce<Record<string, string>>((acc, item) => {
    acc[item.channelId] = item.color;
    return acc;
  }, {});
  const orderedIds = allChannelsMeta
    .map((item) => item.channelId)
    .filter((channelId) => visibleChannelIds.includes(channelId));

  if (!orderedIds.length) {
    const timeValues = stats.timeSeries
      .map((row) => row.time)
      .filter((rawTime): rawTime is string => typeof rawTime === 'string')
      .map((rawTime) => moment(rawTime).tz(timezone).valueOf() / 1000);

    return {
      data: [timeValues] as unknown as number[][],
      series: emptySeries,
      seriesChannelIds: [],
    };
  }

  const timeValues: number[] = [];
  const channelDataMap: Record<string, (number | null)[]> = {};

  orderedIds.forEach((id) => {
    channelDataMap[id] = [];
  });

  stats.timeSeries.forEach((row) => {
    const rawTime = row.time;
    if (typeof rawTime !== 'string') {
      return;
    }
    const timestamp = moment(rawTime).tz(timezone).valueOf() / 1000;
    timeValues.push(timestamp);

    orderedIds.forEach((channelId) => {
      const raw = row[channelId];
      const value =
        typeof raw === 'number' && Number.isFinite(raw) && !Number.isNaN(raw) ? raw : null;
      channelDataMap[channelId].push(value);
    });
  });

  const data: (number | null)[][] = [timeValues];
  orderedIds.forEach((channelId) => {
    data.push(channelDataMap[channelId]);
  });

  const series = orderedIds.map((channelId, index) => ({
    show: true,
    spanGaps: false,
    label: channels[channelId]?.title ?? channelId,
    stroke: colorByChannelId[channelId] ?? pastelColors[index % pastelColors.length],
    width: channels[channelId]?.isSelf ? 3 : 2,
    points: {
      show: false,
    },
  }));

  return { data: data as unknown as number[][], series, seriesChannelIds: orderedIds };
}
